/**
 * Payment utility functions for calculating GST, shipping, and total amounts
 */

// Configuration constants
const GST_RATE = 0.18; // 18% GST
const SHIPPING_RATE_PER_KG = 50; // ₹50 per kg
const MIN_SHIPPING_CHARGE = 100; // Minimum ₹100 shipping
/**
 * Calculates GST amount
 * @param {number} baseAmount - Base amount before GST
 * @returns {number} - GST amount
 */
export const calculateGST = (baseAmount) => {
  return Math.round(baseAmount * GST_RATE * 100) / 100;
};

/**
 * Calculates shipping charges based on weight
 * @param {number} weight - Weight in kg
 * @param {number} baseAmount - Base amount (not used for free shipping calculation)
 * @returns {number} - Shipping charges
 */
export const calculateShippingCharges = (weight = 1, baseAmount = 0) => {
  const shippingByWeight = weight * SHIPPING_RATE_PER_KG;
  return Math.max(shippingByWeight, MIN_SHIPPING_CHARGE);
};

/**
 * Calculates advance payment (10% of base product price)
 * @param {number} productPrice - Base product price (without GST and shipping)
 * @returns {number} - Advance payment amount
 */
export const calculateAdvancePayment = (productPrice) => {
  return Math.round(productPrice * 0.1 * 100) / 100;
};

/**
 * Calculates complete payment breakdown for lot booking
 * @param {number} productPrice - Base product price (total lot price)
 * @param {number} weight - Product weight in kg (default: 1)
 * @param {boolean} isLotBooking - Whether this is a lot booking (affects shipping calculation)
 * @returns {object} - Complete payment breakdown
 */
export const calculatePaymentDetails = (productPrice, weight = 1, isLotBooking = false) => {
  const baseAmount = productPrice;
  
  // For lot booking: Calculate advance amount first, then GST and shipping on advance
  // For normal: Calculate GST and shipping on full amount
  let advanceAmount, gstAmount, shippingCharges, totalAmount, remainingAmount;
  
  if (isLotBooking) {
    // Calculate advance amount (10% of base lot price)
    advanceAmount = calculateAdvancePayment(baseAmount);
    
    // For lot booking: GST and shipping are calculated on advance amount only
    // Shipping is 0 for lot booking items
    gstAmount = calculateGST(advanceAmount);
    shippingCharges = 0; // No shipping for lot booking items
    
    // Total amount for advance payment
    const advanceTotal = advanceAmount + gstAmount + shippingCharges;
    
    // Calculate GST and shipping for full lot (for reference in lot request)
    const fullLotGst = calculateGST(baseAmount);
    const fullLotShipping = calculateShippingCharges(weight, baseAmount);
    const fullLotTotal = baseAmount + fullLotGst + fullLotShipping;
    
    // Remaining amount is full lot total minus advance total
    remainingAmount = fullLotTotal - advanceTotal;
    totalAmount = fullLotTotal;
  } else {
    // Normal calculation
    gstAmount = calculateGST(baseAmount);
    shippingCharges = calculateShippingCharges(weight, baseAmount);
    totalAmount = baseAmount + gstAmount + shippingCharges;
    advanceAmount = calculateAdvancePayment(baseAmount);
    remainingAmount = totalAmount - advanceAmount;
  }
  
  return {
    baseAmount: Math.round(baseAmount * 100) / 100,
    gstAmount: Math.round(gstAmount * 100) / 100,
    gstRate: GST_RATE,
    shippingCharges: Math.round(shippingCharges * 100) / 100,
    totalAmount: Math.round(totalAmount * 100) / 100,
    advanceAmount: Math.round(advanceAmount * 100) / 100,
    remainingAmount: Math.round(remainingAmount * 100) / 100,
    weight: weight,
    // For lot booking: also include full lot GST and shipping for lot request
    fullLotGstAmount: isLotBooking ? Math.round(calculateGST(baseAmount) * 100) / 100 : gstAmount,
    fullLotShippingCharges: isLotBooking ? Math.round(calculateShippingCharges(weight, baseAmount) * 100) / 100 : shippingCharges
  };
};

/**
 * Formats payment details for display
 * @param {object} paymentDetails - Payment details object
 * @returns {object} - Formatted payment details for display
 */
export const formatPaymentDetailsForDisplay = (paymentDetails) => {
  return {
    'Base Amount': `₹${paymentDetails.baseAmount.toFixed(2)}`,
    'GST (18%)': `₹${paymentDetails.gstAmount.toFixed(2)}`,
    'Shipping Charges': `₹${paymentDetails.shippingCharges.toFixed(2)}`,
    'Total Amount': `₹${paymentDetails.totalAmount.toFixed(2)}`,
    'Advance Payment (10%)': `₹${paymentDetails.advanceAmount.toFixed(2)}`,
    'Remaining Amount': `₹${paymentDetails.remainingAmount.toFixed(2)}`
  };
};